import React from 'react';
import { Link } from 'react-router-dom';
import { fullName, money, pct } from '../lib/data';
import AgentChip from './AgentChip';
import SummaryPoints from './SummaryPoints';
import Confidence from './Confidence';
import RiskBadge from './RiskBadge';
import StatusPill from './StatusPill';
import { Button } from './ui/button';
import {
  X, ChevronUp, ChevronDown, ExternalLink, Check, ArrowUpRight, Bot, ShieldAlert,
} from 'lucide-react';

const TYPE_CHIP = { 'New patient': 'pill-indigo', 'Follow-up': 'pill-slate', Procedure: 'pill-violet', Surgery: 'pill-amber' };

// Right side-peek: review a worklist item without leaving the list. Prev/next
// move through the queue; the parent owns the index + keyboard.
export default function PeekDrawer({ item, state, index, total, onPrev, onNext, onClose, onApprove, onEscalate }) {
  if (!item) return null;
  const { patient, payer, plan, provider, apt, check, risk } = item;
  const fin = check?.financials;
  const active = check?.coverage?.active !== false;
  const detailHref = `/app/appointments/${apt.id}`;
  const dedPct = fin?.deductibleInd ? (fin.deductibleIndMet / fin.deductibleInd) * 100 : 0;
  const t = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[1px]" />
      <aside className="dh-drawer-panel relative w-full max-w-[460px] h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Top bar */}
        <div className="h-12 px-3 flex items-center justify-between border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-1">
            <button onClick={onPrev} disabled={index <= 0} className="h-7 w-7 rounded-md hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center"><ChevronUp className="h-4 w-4" /></button>
            <button onClick={onNext} disabled={index >= total - 1} className="h-7 w-7 rounded-md hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center"><ChevronDown className="h-4 w-4" /></button>
            <span className="text-xs text-slate-400 tnum ml-1">{index + 1} of {total}</span>
          </div>
          <div className="flex items-center gap-1">
            <Link to={detailHref} className="text-xs text-brand-600 font-medium hover:underline inline-flex items-center gap-1 mr-1">Open full<ExternalLink className="h-3 w-3" /></Link>
            <button onClick={onClose} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900">{fullName(patient)}</h2>
              <span className={`pill ${TYPE_CHIP[item.visitType] || 'pill-slate'}`}>{item.visitType}</span>
            </div>
            <div className="text-sm text-slate-500 mt-0.5">{t(apt.startTime)} · {apt.reason} · Dr. {provider?.lastName}{item.location ? ` · ${item.location.name}` : ''}</div>
            <div className="flex items-center gap-2 mt-2">
              <RiskBadge level={risk.level} score={risk.score} showScore />
              <StatusPill status={state} />
              {check && <StatusPill status={active ? 'active' : 'inactive'} />}
            </div>
          </div>

          {/* AI summary */}
          {check ? (
            <div className="ai-surface p-3.5">
              <div className="flex items-center justify-between mb-1.5"><AgentChip agent="verification" size="xs" /><Confidence value={0.9} label="parse" /></div>
              <SummaryPoints check={check} provider={provider} payer={payer} size="sm" />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {risk.reasons.map((r, idx) => <span key={idx} className="text-[11px] bg-white/70 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{r}</span>)}
              </div>
            </div>
          ) : (
            <div className="dh-inset p-4 text-sm text-slate-600 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-500" />Not yet verified — agent will run 270/271 when queued.</div>
          )}

          {/* Plan + financials */}
          {check && (
            <div className="dh-card p-4">
              <div className="text-xs font-semibold text-slate-700 mb-2.5">{plan?.planName} · {payer?.name}</div>
              {active ? (
                <div className="grid grid-cols-2 gap-2.5 text-sm">
                  <div className="dh-inset p-2.5"><div className="text-xs text-slate-500">Copay</div><div className="text-base font-semibold text-slate-900 tnum">{money(fin.copay)}</div></div>
                  <div className="dh-inset p-2.5"><div className="text-xs text-slate-500">Coinsurance</div><div className="text-base font-semibold text-slate-900 tnum">{pct(fin.coinsurance)}</div></div>
                  <div className="dh-inset p-2.5 col-span-2">
                    <div className="text-xs text-slate-500">Deductible (individual)</div>
                    <div className="flex items-baseline gap-2 mt-0.5"><span className="text-base font-semibold text-slate-900 tnum">{money(fin.deductibleIndMet)}</span><span className="text-xs text-slate-500">/ {money(fin.deductibleInd)} met</span></div>
                    <div className="mt-1.5 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-brand-500" style={{ width: `${dedPct}%` }} /></div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-rose-700">Coverage inactive — request updated insurance before the visit.</div>
              )}
              <div className="flex gap-2 mt-2.5">
                {[['Prior auth', check.flags.priorAuthRequired], ['Referral', check.flags.referralRequired]].map(([l, on]) => (
                  <span key={l} className={`pill ${on ? 'pill-amber' : 'pill-green'}`}>{l}: {on ? 'Yes' : 'No'}</span>
                ))}
              </div>
            </div>
          )}

          {/* Recommended */}
          <div className="flex items-start gap-2 text-sm">
            <Bot className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
            <div><span className="font-medium text-slate-800">{item.actionLabel || 'No action needed'}</span><div className="text-slate-600">{item.recommended}</div></div>
          </div>
        </div>

        {/* Footer HITL */}
        <div className="border-t border-slate-100 p-3 flex items-center justify-between shrink-0">
          {state === 'awaiting_review' ? (
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white" onClick={onApprove}><Check className="h-4 w-4 mr-1.5" />Approve</Button>
              <Button size="sm" variant="outline" onClick={onEscalate}><ArrowUpRight className="h-4 w-4 mr-1.5" />Escalate</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500"><StatusPill status={state} />Resolved</div>
          )}
          <span className="text-[11px] text-slate-400 hidden sm:flex items-center gap-2"><span className="kbd">↑</span><span className="kbd">↓</span> move <span className="kbd">e</span> approve <span className="kbd">esc</span></span>
        </div>
      </aside>
    </div>
  );
}
